import React, { useEffect, useMemo, useRef, useState } from "react";
import { Shield } from "lucide-react";
import GenericCard from "@ui-components/Card/GenericCard";
import type { Fwaas } from "@shared/components/fwaas/FwaasData";
import type { HaMember, VmSpecs } from "kontrolEdge/components/Shared/VmSpecsCard";
import type { Tunnels } from "../tunnels/tunnels";
import type { FirewallNat } from "../nat/nat.types";
import type { FirewallEvents } from "../events/events.types";
import type { FirewallPolicies } from "../policies/policies.types";
import type { FwaasOverview } from "../overview/overview.types";
import type { FirewallRouting } from "../routing/routing.types";
import type { Reading } from "../readings/useFwaasReading";
import type { AttentionItem } from "../attention/needsAttention";
import CardHeading from "../overview/CardHeading";
import { scrollToElement } from "../detailScroll";
import FirewallInspector from "./FirewallInspector";
import ServiceMapList, { ICON } from "./ServiceMapList";
import { useWindowSize } from "@shared/hooks/useWindowSize";
import { LaneInspector, NodeInspector } from "./NodeInspector";
import { ALERT_TONE } from "./alertTone";
import {
    LANE_HEADER_Y,
    LANE_LABEL,
    LANE_RECT,
    MAP_WIDTH,
    attentionFor,
    buildServiceMap,
    curvePath,
    edgeCurve,
    haPair,
    layoutServiceMap,
    litNodes,
    pillPoint,
    type AlertLevel,
    type Lane,
    type MapLayout,
    type MapNode,
    type MapSelection,
    type Point,
} from "./serviceMap";

const LANES: Lane[] = ["public", "private"];

/** Places a card or a pill on the drawing, in percent so it scales with the canvas. */
const at = (point: Point, height: number): React.CSSProperties => ({
    left: `${(point.x / MAP_WIDTH) * 100}%`,
    top: `${(point.y / height) * 100}%`,
});

const DIMMED = "grayscale opacity-40";

/** How long the card stays outlined after a selection made from outside the map. */
export const REVEAL_OUTLINE_MS = 2500;
const REVEAL_OUTLINE = "ring-semantic-border-brand-primary";

export interface ServiceMapProps {
    service: Fwaas;
    vm: VmSpecs | null;
    haMembers: HaMember[];
    overview: FwaasOverview | null;
    routing: Reading<FirewallRouting>;
    tunnels: Tunnels | null;
    nat: Reading<FirewallNat>;
    policies: Reading<FirewallPolicies>;
    events: Reading<FirewallEvents>;
    attention: AttentionItem[];
    /** The page owns the selection, so the header's pill can set it too. */
    selection: MapSelection | null;
    onSelect: (selection: MapSelection | null) => void;
    /** Bumped when the selection comes from elsewhere on the page: the map comes into view and its inspector takes focus. */
    revealNonce: number;
    /** A rule's name in an inspector: the page shows it in the Policies table. */
    onRuleClick: (ruleId: number) => void;
}

function Edges({ nodes, layout, lit }: { nodes: MapNode[]; layout: MapLayout; lit: Set<string> | null }) {
    return (
        <>
            {nodes.map((node) => {
                const d = curvePath(edgeCurve(layout.firewall, layout.position[node.id]));
                const on = lit === null || lit.has(node.id);
                const tone = node.alert ? ALERT_TONE[node.alert].stroke : "stroke-semantic-fg-brand-primary";
                const stroke = on ? tone : "stroke-semantic-border-secondary";
                return (
                    <g key={node.id} data-edge={node.id}>
                        <path
                            d={d}
                            fill="none"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeDasharray={node.alert || node.edgeStyle === "logical" ? "6 5" : undefined}
                            className={`${stroke} transition-[stroke] duration-200`}
                        />
                        {/* Traffic along the selected line only; still for reduced motion. */}
                        {lit !== null && on && (
                            <path
                                d={d}
                                fill="none"
                                strokeWidth={3}
                                strokeLinecap="round"
                                strokeDasharray="2 12"
                                vectorEffect="non-scaling-stroke"
                                className={`${tone} motion-safe:animate-flow-dash`}
                            />
                        )}
                    </g>
                );
            })}
        </>
    );
}

function EdgePills({ nodes, layout, lit }: { nodes: MapNode[]; layout: MapLayout; lit: Set<string> | null }) {
    return (
        <>
            {nodes.map((node) => {
                const point = pillPoint(edgeCurve(layout.firewall, layout.position[node.id]), node.lane);
                const dimmed = lit !== null && !lit.has(node.id);
                return (
                    <span
                        key={node.id}
                        aria-hidden
                        style={at(point, layout.height)}
                        className={`absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none whitespace-nowrap rounded-full border bg-semantic-bg-primary px-2 font-mono text-[10.5px] transition-[opacity,filter] duration-200 ${
                            node.alert ? `${ALERT_TONE[node.alert].border} ${ALERT_TONE[node.alert].text}` : "border-semantic-border-secondary text-semantic-text-brand"
                        } ${dimmed ? DIMMED : ""}`}
                    >
                        {node.edgeLabel}
                    </span>
                );
            })}
        </>
    );
}

function NodeCard({
    node,
    point,
    height,
    pressed,
    dimmed,
    buttonRef,
    onClick,
}: {
    node: MapNode;
    point: Point;
    height: number;
    pressed: boolean;
    dimmed: boolean;
    buttonRef: (el: HTMLButtonElement | null) => void;
    onClick: () => void;
}) {
    const Icon = ICON[node.icon];
    const tone = node.alert ? ALERT_TONE[node.alert] : null;
    return (
        <button
            ref={buttonRef}
            type="button"
            aria-pressed={pressed}
            aria-label={`${node.name}, ${node.meta}${node.alert ? ", with observations" : ""}`}
            onClick={onClick}
            style={at(point, height)}
            className={`absolute -translate-x-1/2 -translate-y-1/2 w-[204px] flex items-center gap-2.5 rounded-xl border bg-semantic-bg-primary px-3 py-2 text-left transition-[opacity,filter,box-shadow] duration-200 ${
                tone ? tone.border : "border-semantic-border-secondary hover:border-semantic-border-brand-primary"
            } ${pressed ? "ring-4 ring-semantic-bg-brand-secondary border-semantic-border-brand-primary" : ""} ${dimmed ? DIMMED : ""}`}
        >
            <span
                className={`grid place-items-center w-8 h-8 shrink-0 rounded-lg ${
                    tone ? tone.icon : "bg-semantic-bg-brand-secondary text-semantic-text-brand"
                }`}
            >
                <Icon size={18} aria-hidden />
            </span>
            <span className="flex flex-col min-w-0">
                <span className="text-sm font-semibold leading-tight truncate">{node.name}</span>
                <span className={`text-xs truncate ${tone ? tone.text : "text-semantic-text-secondary"}`}>{node.meta}</span>
            </span>
            {tone && (
                <span aria-hidden className="absolute -top-1 -right-1 w-2.5 h-2.5">
                    <span className={`absolute inset-0 rounded-full ${tone.dot} motion-safe:animate-ping opacity-75`} />
                    <span className={`absolute inset-0 rounded-full ${tone.dot} border-2 border-semantic-bg-primary`} />
                </span>
            )}
        </button>
    );
}

function FirewallCard({
    service,
    overview,
    haMembers,
    alert,
    point,
    height,
    pressed,
    buttonRef,
    onClick,
}: {
    service: Fwaas;
    overview: FwaasOverview | null;
    haMembers: HaMember[];
    alert: AlertLevel | null;
    point: Point;
    height: number;
    pressed: boolean;
    buttonRef: (el: HTMLButtonElement | null) => void;
    onClick: () => void;
}) {
    const ha = overview?.ha;
    const pair = haPair(ha, haMembers);
    const sync = ha?.in_sync == null ? "HA" : ha.in_sync ? "HA in sync" : "HA out of sync";
    const mode = ha?.mode === "active-passive" ? "active / passive" : ha?.mode === "active-active" ? "active / active" : ha?.mode === "standalone" ? "standalone" : null;
    const id = service.subscriptionId ?? service.name;
    return (
        <button
            ref={buttonRef}
            type="button"
            aria-pressed={pressed}
            aria-label={`Firewall ${id}${pair.length ? `, ${sync}` : ""}${alert ? ", with observations" : ""}`}
            onClick={onClick}
            style={at(point, height)}
            className={`absolute -translate-x-1/2 -translate-y-1/2 w-[240px] flex flex-wrap items-center gap-x-2.5 gap-y-2 rounded-xl border-[1.5px] bg-semantic-bg-primary px-3.5 pt-3 pb-2.5 text-left transition-shadow duration-200 ${
                alert ? ALERT_TONE[alert].border : "border-semantic-border-brand-primary"
            } ${pressed ? "ring-4 ring-semantic-bg-brand-secondary" : ""}`}
        >
            <span className="grid place-items-center w-[38px] h-[38px] shrink-0 rounded-lg bg-semantic-bg-brand-bold text-semantic-text-inverse-primary">
                <Shield size={20} aria-hidden />
            </span>
            <span className="flex flex-col min-w-0">
                <span className="font-mono text-sm truncate">{id}</span>
                <span className="text-xs text-semantic-text-secondary truncate">{["Virtual firewall", mode].filter(Boolean).join(" · ")}</span>
            </span>
            {pair.length > 0 && (
                <span aria-hidden className="flex items-center gap-1.5 w-full border-t border-semantic-border-primary pt-2 text-xs text-semantic-text-secondary">
                    {pair.map((member, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && <span className={`w-3.5 border-t-[1.5px] border-dashed ${ha?.in_sync === false ? "border-semantic-fg-warning" : "border-semantic-fg-success"}`} />}
                            <span
                                className={`grid place-items-center w-[22px] h-[18px] rounded font-mono text-[10.5px] border-[1.5px] ${
                                    member.up ? "border-semantic-fg-success" : "border-semantic-fg-danger"
                                } ${
                                    member.letter === "A" && member.up
                                        ? "bg-semantic-fg-success text-semantic-text-inverse-primary"
                                        : member.up
                                          ? "text-semantic-text-success"
                                          : "text-semantic-text-danger"
                                }`}
                            >
                                {member.letter}
                            </span>
                        </React.Fragment>
                    ))}
                    <span className="ml-1">{sync}</span>
                </span>
            )}
        </button>
    );
}

/** Which inspector a selection opens. */
function Inspector({
    selection,
    nodes,
    map,
    titleRef,
    onClose,
}: {
    selection: MapSelection;
    nodes: MapNode[];
    map: ServiceMapProps;
    titleRef: React.Ref<HTMLHeadingElement>;
    onClose: () => void;
}) {
    const attention = attentionFor(selection, map.attention, nodes);
    if (selection === "firewall") {
        return (
            <FirewallInspector
                service={map.service}
                vm={map.vm}
                haMembers={map.haMembers}
                overview={map.overview}
                routing={map.routing}
                tunnels={map.tunnels}
                events={map.events}
                nodes={nodes}
                attention={attention}
                titleRef={titleRef}
                onClose={onClose}
            />
        );
    }
    if (selection.startsWith("lane:")) {
        return <LaneInspector lane={selection.slice("lane:".length) as Lane} nodes={nodes} titleRef={titleRef} onSelect={map.onSelect} onClose={onClose} />;
    }
    const node = nodes.find((n) => n.id === selection);
    if (!node) return null;
    const readings = { tunnels: map.tunnels, nat: map.nat, policies: map.policies, events: map.events, onRuleClick: map.onRuleClick };
    return (
        <NodeInspector
            node={node}
            nodes={nodes}
            readings={readings}
            attention={attention}
            titleRef={titleRef}
            onSelect={map.onSelect}
            onClose={onClose}
        />
    );
}

/**
 * What a selection shows on this map. A node that is not drawn — an alert about Internet
 * on a firewall without one, a stale `?node=` — falls back on the firewall, like the
 * map's amber does.
 */
function standingSelection(selection: MapSelection | null, nodes: MapNode[]): MapSelection | null {
    if (selection === null || selection === "firewall") return selection;
    if (selection === "lane:public" || selection === "lane:private") return selection;
    return nodes.some((n) => n.id === selection) ? selection : "firewall";
}

function selectionName(selection: MapSelection, nodes: MapNode[]): string {
    if (selection === "firewall") return "the firewall";
    if (selection.startsWith("lane:")) return LANE_LABEL[selection.slice("lane:".length) as Lane];
    return nodes.find((n) => n.id === selection)?.name ?? selection;
}

/**
 * The service map (CTX-1209, approved design v5): the firewall between what reaches it
 * from the internet and the customer's private network. Drawn by hand — cards placed on
 * a fixed drawing plus SVG curves, as in the design — rather than with ReactFlow: the
 * layout is two fixed lanes, nobody drags anything, and the cards are plain buttons.
 *
 * Selecting a node or a lane dims the rest and opens its inspector beside the map; the
 * firewall is selected when the page opens. ✕, Escape or a click on empty space close it.
 */
export default function ServiceMapCard(props: ServiceMapProps) {
    const { service, overview, haMembers, tunnels, nat, attention, onSelect, revealNonce } = props;
    const model = useMemo(() => buildServiceMap({ service, tunnels, nat, attention }), [service, tunnels, nat, attention]);
    const layout = useMemo(() => layoutServiceMap(model.nodes), [model.nodes]);
    const selection = standingSelection(props.selection, model.nodes);
    const lit = litNodes(selection, model.nodes);
    // The drawing needs 760 px: a phone gets the same map as a list (CTX-1479).
    const isPhone = useWindowSize().width < 640;

    const buttons = useRef(new Map<string, HTMLButtonElement>());
    const sectionRef = useRef<HTMLElement>(null);
    const inspectorRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);

    const [revealed, setRevealed] = useState(false);

    // Selected from elsewhere (the header's pill): bring the map into view and
    // put focus on what opened, so a keyboard user lands on it too. The card is outlined
    // for a moment (CTX-1292): with the map already on screen the scroll does nothing, and
    // the outline is what tells the user the click landed here. A second click restarts it.
    useEffect(() => {
        if (revealNonce === 0) return;
        scrollToElement(sectionRef.current);
        titleRef.current?.focus({ preventScroll: true });
        setRevealed(true);
        const timer = window.setTimeout(() => setRevealed(false), REVEAL_OUTLINE_MS);
        return () => window.clearTimeout(timer);
    }, [revealNonce]);
    const register = (key: string) => (el: HTMLButtonElement | null) => {
        if (el) buttons.current.set(key, el);
        else buttons.current.delete(key);
    };

    const toggle = (key: MapSelection) => onSelect(selection === key ? null : key);
    const close = () => {
        if (selection === null) return;
        // Focus goes back to what opened the inspector, not to the top of the page.
        const focusWasInside = inspectorRef.current?.contains(document.activeElement) ?? false;
        onSelect(null);
        if (focusWasInside) buttons.current.get(selection)?.focus();
    };
    const onCanvasClick = (event: React.MouseEvent) => {
        if (!(event.target as Element).closest("button")) close();
    };
    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Escape" && selection !== null) {
            event.preventDefault();
            close();
        }
    };

    const laneLit = (lane: Lane) => lit === null || model.nodes.some((n) => n.lane === lane && lit.has(n.id));

    return (
        <GenericCard
            isCollapsible={false}
            variant="primary"
            shadow="sm"
            // A ring, not a border, so the outline moves nothing; only its color changes.
            className={`ring-2 motion-safe:duration-500 motion-reduce:transition-none ${revealed ? REVEAL_OUTLINE : "ring-transparent"}`}
        >
            <section
                ref={sectionRef}
                aria-label="Service topology"
                onKeyDown={onKeyDown}
                className={`grid grid-cols-1 ${selection !== null ? "min-[1440px]:grid-cols-[minmax(0,1fr)_348px]" : ""}`}
            >
                <div className="min-w-0 flex flex-col">
                    <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2 px-5 pt-4 pb-1">
                        <CardHeading
                            title="Service topology"
                            subtitle={isPhone ? "Tap a row for its details." : "Select a node or a side for details. Esc closes them."}
                        />
                        {/* The line styles only mean something on the drawing. */}
                        <div aria-hidden className={`${isPhone ? "hidden" : "flex"} flex-wrap gap-4 text-xs text-semantic-text-secondary`}>
                            <span className="inline-flex items-center gap-2">
                                <i className="w-[22px] border-t-2 border-semantic-fg-brand-primary" />
                                Service connection
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <i className="w-[22px] border-t-2 border-dashed border-semantic-fg-brand-primary" />
                                Published or tunnel
                            </span>
                            {/* Both levels, named as the items' tags name them (CTX-1526). */}
                            <span className="inline-flex items-center gap-2">
                                <i className={`w-[22px] border-t-2 border-dashed ${ALERT_TONE.critical.dash}`} />
                                Critical
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <i className={`w-[22px] border-t-2 border-dashed ${ALERT_TONE.attention.dash}`} />
                                Attention
                            </span>
                        </div>
                    </div>
                    {isPhone ? (
                    <ServiceMapList
                        service={service}
                        overview={overview}
                        haMembers={haMembers}
                        firewallAlert={model.firewallAlert}
                        nodes={model.nodes}
                        selection={selection}
                        onToggle={toggle}
                    />
                    ) : (
                    <div className="overflow-x-auto px-3 pb-3.5">
                        {/* A click on empty space closes the inspector; from the keyboard that is Escape. */}
                        <div
                            className="relative min-w-[760px]"
                            style={{ aspectRatio: `${MAP_WIDTH} / ${layout.height}` }}
                            onClick={onCanvasClick}
                            data-testid="service-map-canvas"
                        >
                            <svg viewBox={`0 0 ${MAP_WIDTH} ${layout.height}`} className="absolute inset-0 w-full h-full overflow-visible" aria-hidden>
                                {LANES.map((lane) => (
                                    <rect
                                        key={lane}
                                        x={LANE_RECT[lane].x}
                                        y={layout.laneTop}
                                        width={LANE_RECT[lane].width}
                                        height={layout.laneHeight}
                                        rx={18}
                                        className={`fill-semantic-bg-secondary transition-opacity duration-200 ${laneLit(lane) ? "" : "opacity-50"}`}
                                    />
                                ))}
                                <Edges nodes={model.nodes} layout={layout} lit={lit} />
                            </svg>
                            <EdgePills nodes={model.nodes} layout={layout} lit={lit} />
                            <div role="group" aria-label="Firewall and its connections">
                                {/* The lane headers are the filters: the structure of the map is the control. */}
                                {LANES.map((lane) => {
                                    const key: MapSelection = `lane:${lane}`;
                                    return (
                                        <button
                                            key={lane}
                                            ref={register(key)}
                                            type="button"
                                            aria-pressed={selection === key}
                                            onClick={() => toggle(key)}
                                            style={at({ x: layout.laneX[lane], y: LANE_HEADER_Y }, layout.height)}
                                            className={`absolute -translate-x-1/2 -translate-y-1/2 inline-flex items-center gap-2 whitespace-nowrap rounded-full border py-1 pl-3 pr-1.5 text-xs font-semibold uppercase tracking-wider transition-[opacity,filter,background-color] duration-200 ${
                                                selection === key
                                                    ? "border-transparent bg-semantic-bg-brand-secondary text-semantic-text-brand"
                                                    : "border-transparent text-semantic-text-secondary hover:border-semantic-border-secondary hover:bg-semantic-bg-primary hover:text-semantic-text-primary"
                                            } ${laneLit(lane) ? "" : DIMMED}`}
                                        >
                                            {LANE_LABEL[lane]}
                                            <span className="rounded-full border border-semantic-border-primary bg-semantic-bg-primary px-1.5 font-mono text-[11px] normal-case tracking-normal">
                                                {model.nodes.filter((n) => n.lane === lane).length}
                                            </span>
                                        </button>
                                    );
                                })}
                                <FirewallCard
                                    service={service}
                                    overview={overview}
                                    haMembers={haMembers}
                                    alert={model.firewallAlert}
                                    point={layout.firewall}
                                    height={layout.height}
                                    pressed={selection === "firewall"}
                                    buttonRef={register("firewall")}
                                    onClick={() => toggle("firewall")}
                                />
                                {model.nodes.map((node) => (
                                    <NodeCard
                                        key={node.id}
                                        node={node}
                                        point={layout.position[node.id]}
                                        height={layout.height}
                                        pressed={selection === node.id}
                                        dimmed={lit !== null && !lit.has(node.id)}
                                        buttonRef={register(node.id)}
                                        onClick={() => toggle(node.id)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                    )}
                </div>
                {selection !== null && (
                    <aside
                        ref={inspectorRef}
                        aria-label="Details"
                        className="relative min-w-0 bg-semantic-bg-secondary border-t min-[1440px]:border-t-0 min-[1440px]:border-l border-semantic-border-primary"
                    >
                        {/* Beside the map, the map sets the height and the inspector scrolls
                            inside it (CTX-1272): a long inspector no longer stretches the card.
                            Stacked under it on narrower screens, it flows as before. */}
                        <div className="flex flex-col gap-4 px-5 py-4 min-[1440px]:absolute min-[1440px]:inset-0 min-[1440px]:overflow-y-auto">
                            <Inspector selection={selection} nodes={model.nodes} map={props} titleRef={titleRef} onClose={close} />
                        </div>
                    </aside>
                )}
                <p className="sr-only" aria-live="polite">
                    {selection === null ? "Details closed" : `Showing details for ${selectionName(selection, model.nodes)}`}
                </p>
            </section>
        </GenericCard>
    );
}
